import { generatePulse } from "$game/pulse";
import { computeMatches, computeTwinRate } from "$game/sync";
import {
  echoTier as deriveEchoTier,
  loadEcho,
  pruneEchoes,
  pulseSignature,
  saveEcho,
} from "$game/echo";
import { popNextCategory } from "$game/category-queue";
import { CATEGORIES } from "$data/categories";
import { profile } from "$stores/profile.svelte";
import { pair } from "$stores/pair.svelte";
import {
  computeBadges,
  computeXpThisPulse,
  levelInfoFor,
  type Badge,
} from "$data/leveling";
import type { Pulse } from "$data/types";

type Screen =
  | "intro"
  | "play"
  | "wait"
  | "reveal"
  | "share"
  | "scoreboard"
  | "bond"
  | "settings";
export type Mode = "solo" | "bonded";

/** djb2 → 32-bit signed int. Stable across runtimes (no crypto polyfill). */
function hash32(input: string): number {
  let h = 5381 >>> 0;
  for (let i = 0; i < input.length; i++) {
    h = (((h << 5) + h) ^ input.charCodeAt(i)) >>> 0;
  }
  return h | 0;
}

/** ISO YYYY-MM-DD for the current UTC day. Drives solo content stability. */
function todayUTC(): string {
  return new Date().toISOString().slice(0, 10);
}

class GameState {
  // Navigation
  screen = $state<Screen>("intro");
  /** Where to return when leaving scoreboard. */
  scoreboardOrigin = $state<Screen>("intro");

  /** Friend's display name. Resolves to the active twin's handle when bonded;
   *  empty string when there's no current twin (the streak bar gates on
   *  `pair.isBonded` so this is never user-visible pre-bond). The setter
   *  exists for the simulator path where we may want to override locally. */
  private _friendOverride = $state<string>("");
  get friendHandle(): string {
    return this._friendOverride || pair.friendHandle || "";
  }
  set friendHandle(v: string) {
    this._friendOverride = v;
  }

  /** Lifetime stats live on the profile store. These getters expose them
   *  through the existing `game.userHandle` / `game.streak` API so older
   *  reads keep working without a sweeping rename. */
  get userHandle(): string {
    return profile.handle;
  }
  set userHandle(v: string) {
    profile.setHandle(v);
  }
  get streak(): number {
    return profile.streak;
  }

  /** Current play mode. Solo branch never touches the relay or Wait screen. */
  mode = $state<Mode>("solo");

  // Current Pulse
  pulse = $state<Pulse | null>(null);
  /** Stable signature for the current pulse content — used by Echo Self. */
  pulseSig = $state<string>("");
  selected = $state<string | null>(null);
  userOrder = $state<(string | null)[]>([null, null, null, null, null]);
  friendOrder = $state<string[]>([]);
  syncPct = $state<number>(0);
  matches = $state<boolean[]>([]);

  // Echo Self (solo mode only)
  /** Null = first play of this content (baseline). Number = computed echo %. */
  echoPct = $state<number | null>(null);
  echoTierLabel = $state<string>("");
  /** True when this is the first time the user has played this content. */
  echoBaseline = $state<boolean>(false);
  /** Set by Wait.svelte when bonded relay times out without a friend ct. */
  pendingFriend = $state<boolean>(false);

  // Currency / leveling / badges (additive — see $data/leveling).
  // Defaults match the spec's example numbers (LVL 7, 240/380 toward LVL 8)
  // so the Intro level card has visible state pre-play.
  xp = $state<number>(240);
  level = $state<number>(7);
  levelTitle = $state<string>("BFF");
  xpForLevel = $state<number>(240); // floor of current level
  xpForNext = $state<number>(380); // floor of next level
  xpToNext = $state<number>(140);
  xpThisPulse = $state<number>(0);
  earnedBadges = $state<Badge[]>([]);

  // Derived
  filledCount = $derived(this.userOrder.filter((s) => s !== null).length);
  isReady = $derived(this.filledCount === 5);

  goto(s: Screen) {
    if (this.screen === s) return;
    const swap = () => {
      this.screen = s;
    };
    // View Transitions API (Safari 18+, Chrome 111+) gives a GPU-only
    // cross-fade between screens. Older engines just swap instantly.
    type DocVT = Document & {
      startViewTransition?: (cb: () => void) => { ready: Promise<void> };
    };
    const doc =
      typeof document !== "undefined" ? (document as DocVT) : undefined;
    const reduceMotion =
      typeof matchMedia !== "undefined" &&
      matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (doc?.startViewTransition && !reduceMotion) {
      doc.startViewTransition(swap);
    } else {
      swap();
    }
  }

  /**
   * Begin a new pulse. Bonded passes the global daily seed (same content for
   * both twins). Solo pops the next category from the rotation queue and
   * derives a content seed from `(today_UTC, categoryId)` so any replays of
   * that category within the same UTC day match an echo signature.
   */
  startNewPulse(opts: { seed?: number; mode?: Mode } | number = {}) {
    // Back-compat: a bare number means bonded daily seed.
    const o =
      typeof opts === "number" ? { seed: opts, mode: "bonded" as Mode } : opts;
    const mode: Mode = o.mode ?? "solo";
    this.mode = mode;

    if (mode === "bonded") {
      this.pulse = generatePulse({ seed: o.seed });
    } else {
      const allIds = CATEGORIES.map((c) => c.id);
      const categoryId = popNextCategory(allIds);
      const contentSeed = hash32(`${todayUTC()}|${categoryId}`);
      // `seed` here is only used for the human-readable pulse-id label; the
      // actual content (elements + prompt) is driven by `contentSeed`.
      const idSeed = (Date.now() & 0x7fffffff) >>> 0;
      this.pulse = generatePulse({
        seed: idSeed,
        contentSeed,
        forceCategoryId: categoryId,
      });
    }

    this.pulseSig = this.pulse ? pulseSignature(this.pulse) : "";
    this.selected = null;
    this.userOrder = [null, null, null, null, null];
    this.friendOrder = [];
    this.syncPct = 0;
    this.matches = [];
    this.echoPct = null;
    this.echoTierLabel = "";
    this.echoBaseline = false;
    this.pendingFriend = false;
  }

  selectShape(id: string) {
    if (this.userOrder.includes(id)) return;
    this.selected = id;
  }

  tapSlot(idx: number) {
    if (this.selected) {
      const next = [...this.userOrder];
      next[idx] = this.selected;
      this.userOrder = next;
      this.selected = null;
    } else if (this.userOrder[idx]) {
      const next = [...this.userOrder];
      next[idx] = null;
      this.userOrder = next;
    }
  }

  clearAll() {
    this.userOrder = [null, null, null, null, null];
    this.selected = null;
  }

  /**
   * Submit the user's ranking. Branches by mode:
   *  - solo: compare against the saved echo (or set baseline). No Wait screen,
   *    no fake friend. Result is private (never published to scoreboard).
   *  - bonded: legacy path used only when the relay seal failed; computes
   *    nothing locally and routes to Wait.
   */
  submit() {
    const u = this.userOrder.filter((x): x is string => x !== null);
    if (u.length !== 5) return;

    if (this.mode === "solo") {
      const sig = this.pulseSig;
      const prev = sig ? loadEcho(sig) : null;

      if (prev && prev.length === 5) {
        this.echoBaseline = false;
        this.friendOrder = prev; // reused for match-highlights in Reveal
        const pct = computeTwinRate(u, prev);
        this.echoPct = pct;
        this.echoTierLabel = deriveEchoTier(pct);
        this.syncPct = pct;
        this.matches = computeMatches(u, prev);
      } else {
        this.echoBaseline = true;
        this.echoPct = null;
        this.echoTierLabel = "";
        this.friendOrder = [];
        this.syncPct = 0;
        this.matches = [];
      }

      if (sig) saveEcho(sig, u);
      pruneEchoes();

      profile.recordPulse();
      this.finaliseRewards();
      this.goto("reveal");
      return;
    }

    // Bonded fallback: relay seal failed at the call-site (Play.svelte), so
    // there is no friend ct to wait on. Show a pending state on Wait so we
    // never fabricate a friend's ranking.
    this.friendOrder = [];
    this.syncPct = 0;
    this.matches = [];
    this.pendingFriend = true;
    profile.recordPulse();
    if (pair.current) pair.recordPulse(pair.current.pairId);
    this.goto("wait");
  }

  /** Submit without computing — for the paired flow where we wait on the
   *  friend's encrypted blob to arrive via the relay. Streak still ticks. */
  submitDeferred() {
    const u = this.userOrder.filter((x): x is string => x !== null);
    if (u.length !== 5) return false;
    this.friendOrder = [];
    this.syncPct = 0;
    this.matches = [];
    profile.recordPulse();
    if (pair.current) pair.recordPulse(pair.current.pairId);
    this.goto("wait");
    return true;
  }

  /** Apply a friend's resolved ordering (from the relay) and compute sync. */
  applyFriendOrder(friendOrder: string[]) {
    const u = this.userOrder.filter((x): x is string => x !== null);
    if (u.length !== 5 || friendOrder.length !== 5) return;
    this.friendOrder = friendOrder;
    this.syncPct = computeTwinRate(u, friendOrder);
    this.matches = computeMatches(u, friendOrder);
    this.finaliseRewards();
  }

  /** Compute XP + badges + level transition for this Pulse outcome.
   *  Idempotent for a given (syncPct, matches, streak) tuple — safe to call
   *  from both the sim path (submit) and the relay path (applyFriendOrder).
   */
  private finaliseRewards() {
    const cat = this.pulse?.categoryId ?? "shapes";
    const catLabel = this.pulse?.categoryLabel ?? "PULSE";
    const streak = profile.streak;
    const xpDelta = computeXpThisPulse({
      syncPct: this.syncPct,
      categoryId: cat,
      streak,
    });
    this.xpThisPulse = xpDelta;
    const prevLevel = this.level;
    const nextXp = this.xp + xpDelta;
    const info = levelInfoFor(nextXp);
    this.xp = nextXp;
    this.level = info.level;
    this.levelTitle = info.title;
    this.xpForLevel = info.xpForLevel;
    this.xpForNext = info.xpForNext;
    this.xpToNext = info.xpToNext;
    this.earnedBadges = computeBadges({
      syncPct: this.syncPct,
      matches: this.matches,
      categoryLabel: catLabel,
      streak,
      tieredUp: info.level > prevLevel,
    });
  }

  reset() {
    // Streak and pulses live on profile and reflect real history — preserve.
    this.pulse = null;
    this.pulseSig = "";
    this.selected = null;
    this.userOrder = [null, null, null, null, null];
    this.friendOrder = [];
    this.syncPct = 0;
    this.matches = [];
    this.echoPct = null;
    this.echoTierLabel = "";
    this.echoBaseline = false;
    this.pendingFriend = false;
    this.goto("intro");
  }
}

export const game = new GameState();
